import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useTransform, useSpring, useScroll, useMotionValueEvent, MotionValue, Variants } from 'framer-motion';
import { LucideIcon } from 'lucide-react';
import {
  Users, Building2, Clock3, ShieldCheck, Handshake,
  Phone, MessageCircle, Mail, ArrowRight, Stethoscope,
  Microscope, Home as HomeIcon, Pill, GraduationCap, Trophy, Wrench, Leaf,
  Heart, Lightbulb, TrendingUp
} from 'lucide-react';
import { stock, verticalIcons } from '../data/media';
import { verticals } from '../data/verticals';
import Reveal from '../components/Reveal';
import SafeImage from '../components/SafeImage';
import { useIsMobile } from '../hooks/useIsMobile';

const WA_URL = 'https://wa.me/919789890769?text=Hello%20Reach%20Groups%2C%20I%20would%20like%20to%20enquire%20about%20your%20services.';
const journey = [
  { year: '2008', label: 'Clinical Practice', img: stock.tl1, title: 'Compassionate', sub: 'We care like family', icon: Building2 },
  { year: '2015', label: 'Medical Workshops & Training', img: stock.tl2, title: 'Innovative', sub: 'We adopt, adapt and advance', icon: Users },
  { year: '2020', label: 'Social Welfare Initiatives', img: stock.tl3, title: 'Trustworthy', sub: 'Built on transparency and ethics', icon: Handshake },
  { year: '2026', label: 'Reach Groups Established', img: stock.tl4, title: 'Community First', sub: 'We serve beyond expectations', icon: TrendingUp },
];

const whyChoose = [
  { Icon: Microscope, title: 'Advanced Diagnostics', desc: 'State-of-the-art MRI, CT, and lab facilities with results in 4–6 hours and home sample collection.' },
  { Icon: Stethoscope, title: 'Multispeciality Clinics', desc: 'Panel of 20+ specialists delivering same-day consultations across 10+ clinical disciplines.' },
  { Icon: HomeIcon, title: 'Home Healthcare', desc: 'Professional nursing, physiotherapy and caretaker services at your doorstep 24/7.' },
  { Icon: Pill, title: 'Integrated Pharmacy', desc: 'Full-service pharmacy with genuine medicines, generic alternatives and home delivery.' },
  { Icon: GraduationCap, title: 'Healthcare Education', desc: 'Paramedical and nursing training programmes with placement support across Reach Group verticals.' },
  { Icon: Trophy, title: 'Sports & Wellness', desc: 'Elite sports coaching backed by sports medicine, nutrition counselling and modern facilities.' },
  { Icon: Wrench, title: 'Medical Supplies', desc: 'Comprehensive surgical and medical equipment supply to institutions across Chennai.' },
  { Icon: Leaf, title: 'Wellness Programmes', desc: 'Nature-based therapeutic programmes complementing conventional medical treatment.' },
];

const impactNodes = [
  { img: stock.hch1, size: 276, desc: 'Dedication and leadership.', focus: 'center' },
  { img: stock.hch2, size: 256, desc: 'Together, we celebrate success.', focus: 'center' },
  { img: stock.hch3, size: 256, desc: 'Commitment and Achievement.', focus: 'center' },
  { img: stock.hch4, size: 276, desc: 'Growing together, serving better.', focus: 'center' },
];

// Value icons available in global scope to fix TS errors
const valueIcons = [Heart, Lightbulb, ShieldCheck, Users];

// Explicit per-vertical domains, keyed by `id` — TLDs vary (.com / .in / .co.in)
// so a guessed pattern doesn't work; these are placeholders, swap in the real
// domains whenever you have them. `v.website` on the vertical itself (in
// verticals.ts) still wins if it's set, so you can override per-vertical there
// instead of editing this map.
const VERTICAL_WEBSITES: Record<string, string> = {
  bms: 'reachmedinscansandlabs.com',
  rmc: 'https://reachmultispecialityclinic.com/',
  rhms: 'https://reachhomecare.in/',
  rp: 'reachpharma.in',
  rsp: 'reachsurgicals.co.in',
  academy: 'reachacademy.co.in',
  rsl: 'reachscansandlabs.com',
  impex: 'https://reachimpex.co/',
  gardens: 'reachgardens.in',
  rsa: 'reachsportsacademy.co.in',
};

const urlForVertical = (v: typeof verticals[number]): string => {
  const domain = v.website || VERTICAL_WEBSITES[v.id];
  if (!domain) return '#';
  return domain.startsWith('http') ? domain : `https://www.${domain}`;
};

// --- VARIANTS ---
const heroTextContainer: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.1 } }
};

const heroWord: Variants = {
  hidden: { opacity: 0, y: 15, filter: 'blur(10px)' },
  show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1] } }
};

// Shuffled Card Animation for the Hero Metrics
const cardShuffle: Variants = {
  hidden: { opacity: 0, x: 80, y: 60, rotate: 20, scale: 0.6 },
  show: { opacity: 1, x: 0, y: 0, rotate: 0, scale: 1, transition: { type: 'spring', damping: 15, stiffness: 110 } }
};

const JourneyWave: React.FC<{ items: typeof journey; revealed: number; drawProgress: MotionValue<number>; reduceMotion: boolean }> = ({ items, revealed, drawProgress, reduceMotion }) => {
  const W = 1600, BASE_Y = 380, RADIUS = 126;
  const n = items.length;
  const xs = items.map((_, i) => 150 + (i / (n - 1)) * (W - 300));
  const waveYs = items.map((_, i) => BASE_Y + (i % 2 === 0 ? -20 : 20));
  const circleYs = items.map((_, i) => 200 + (i % 2 === 0 ? 0 : 40));

  const path = xs.reduce((acc, x, i) => {
    if (i === 0) return `M ${x} ${waveYs[i]}`;
    const px = xs[i - 1], py = waveYs[i - 1];
    const mx = (px + x) / 2;
    return `${acc} C ${mx} ${py}, ${mx} ${waveYs[i]}, ${x} ${waveYs[i]}`;
  }, '');

  return (
    <svg viewBox={`0 0 ${W} 500`} style={{ width: '100%', height: 'auto', overflow: 'visible' }}>
      <defs>
        <linearGradient id="jw-line" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#005B66" />
          <stop offset="100%" stopColor="#D62828" />
        </linearGradient>
        <filter id="jw-glow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="12" />
        </filter>
      </defs>

      {/* Main Base Path */}
      <motion.path
        d={path} fill="none" stroke="url(#jw-line)" strokeWidth={3} strokeDasharray="2 12" strokeLinecap="round"
        style={{ pathLength: drawProgress }}
      />
      
      {/* Continuous Energy Pulse travelling through the path — skipped on
          mobile/reduced-motion, it's a purely decorative infinite loop */}
      {!reduceMotion && (
        <motion.path
          d={path} fill="none" stroke="#D62828" strokeWidth={4} strokeLinecap="round"
          animate={{ strokeDasharray: ["0 1600", "200 1600"], strokeDashoffset: [0, -1600] }}
          transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
          style={{ opacity: 0.6, filter: 'drop-shadow(0 0 8px rgba(214,40,40,0.4))' }}
        />
      )}

      {items.map((it, i) => {
        const Icon = it.icon;
        const isLast = i === n - 1;
        const isOn = revealed > i;
        return (
          <g
            key={it.year}
            style={{
              opacity: isOn ? 1 : 0,
              transform: isOn ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.8)',
              transformOrigin: `${xs[i]}px ${circleYs[i]}px`,
              transition: 'opacity 0.7s cubic-bezier(.16,1,.3,1), transform 0.7s cubic-bezier(.16,1,.3,1)',
            }}
          >
            <line x1={xs[i]} y1={circleYs[i] + RADIUS} x2={xs[i]} y2={waveYs[i]} stroke="rgba(0,91,102,0.2)" strokeWidth={2} strokeDasharray="3 6" />
            
            {/* Soft Breathing Checkpoint Aura — skipped on mobile */}
            {!reduceMotion && (
              <motion.circle 
                animate={{ scale: [1, 1.5, 1], opacity: [0.6, 0.1, 0.6] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: i * 0.4 }}
                cx={xs[i]} cy={waveYs[i]} r={10} fill="none" stroke={isLast ? '#D62828' : '#005B66'} strokeWidth={2} 
              />
            )}
            <circle cx={xs[i]} cy={waveYs[i]} r={6} fill={isLast ? '#D62828' : '#005B66'} stroke="#fff" strokeWidth={2.5} />

            <foreignObject x={xs[i] - 40} y={waveYs[i] + 16} width={80} height={26}>
              <div style={{ textAlign: 'center', fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: 14, color: isLast ? '#D62828' : '#005B66' }}>
                {it.year}
              </div>
            </foreignObject>

            <circle cx={xs[i]} cy={circleYs[i]} r={RADIUS + 6} fill="rgba(0,20,22,0.15)" filter="url(#jw-glow)" />

            <foreignObject x={xs[i] - RADIUS} y={circleYs[i] - RADIUS} width={RADIUS * 2} height={RADIUS * 2}>
              <div style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', border: '6px solid #fff', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', transform: 'translateZ(0)', WebkitBackfaceVisibility: 'hidden' }}>
                <SafeImage src={it.img} alt={it.label} style={{ objectFit: 'contain', background: '#f7f9fa' }} />
              </div>
            </foreignObject>

            <foreignObject x={xs[i] - 120} y={circleYs[i] - RADIUS - 85} width={240} height={65}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid rgba(0,91,102,0.12)', borderRadius: 16, padding: '10px 16px', boxShadow: '0 12px 24px rgba(0,20,22,0.06)' }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#e8f4f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={20} color="#005B66" />
                </div>
                <div style={{
                  fontSize: 13, color: '#1a1a1a', fontWeight: 700, lineHeight: 1.3,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>{it.label}</div>
              </div>
            </foreignObject>
          </g>
        );
      })}
    </svg>
  );
};

const DnaParticles: React.FC<{ disabled?: boolean }> = ({ disabled }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (disabled) return; // mobile/reduced-motion — skip the whole rAF loop
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const mouse = { x: -9999, y: -9999 };
    let width = 0, height = 0, flow = 0, raf = 0;

    interface Particle { bx: number; strand: 0 | 1; x: number; y: number; r: number; }
    let particles: Particle[] = [];
    const SPACING = 8, PERIOD = 510;

    const build = () => {
      particles = [];
      const count = Math.ceil(width / SPACING) + 4;
      for (let i = 0; i < count; i++) {
        const bx = i * SPACING - SPACING * 2;
        particles.push({ bx, strand: 0, x: bx, y: height / 2, r: 2 + Math.random() * 1.6 });
        particles.push({ bx, strand: 1, x: bx, y: height / 2, r: 2 + Math.random() * 1.6 });
      }
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width; height = rect.height;
      canvas.width = width * dpr; canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
    };

    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };
    const onLeave = () => { mouse.x = -9999; mouse.y = -9999; };

    const REPEL_RADIUS = 100, REPEL_STRENGTH = 42;

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      flow += 0.55;
      const amplitude = height * 0.32, midY = height / 2;

      for (const p of particles) {
        const phase = ((p.bx + flow) / PERIOD) * Math.PI * 2;
        const baseX = p.bx;
        const baseY = midY + Math.sin(phase + (p.strand ? Math.PI : 0)) * amplitude;

        let targetX = baseX, targetY = baseY;
        const dx = baseX - mouse.x, dy = baseY - mouse.y;
        const dist = Math.hypot(dx, dy) || 1;
        if (dist < REPEL_RADIUS) {
          const force = (1 - dist / REPEL_RADIUS) * REPEL_STRENGTH;
          targetX = baseX + (dx / dist) * force;
          targetY = baseY + (dy / dist) * force;
        }
        p.x += (targetX - p.x) * 0.14;
        p.y += (targetY - p.y) * 0.14;
      }

      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(11,112,120,0.14)';
      for (let i = 0; i < particles.length; i += 2) {
        if ((i / 2) % 4 !== 0) continue;
        const a = particles[i], b = particles[i + 1];
        if (!a || !b || a.x < -20 || a.x > width + 20) continue;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }

      for (const p of particles) {
        if (p.x < -10 || p.x > width + 10) continue;
        ctx.beginPath();
        ctx.fillStyle = p.strand === 0 ? 'rgba(0,91,102,0.55)' : 'rgba(214,40,40,0.42)';
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('resize', resize);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
    };
  }, [disabled]);

  return <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 0, opacity: disabled ? 0.4 : 1, background: disabled ? 'linear-gradient(90deg, rgba(0,91,102,0.06), rgba(214,40,40,0.05))' : 'transparent' }} aria-hidden />;
};

const HEX_CLIP = 'polygon(25% 3%, 75% 3%, 100% 50%, 75% 97%, 25% 97%, 0% 50%)';
const HEX_W = 194, HEX_H = 194, HONEYCOMB_COL_W = HEX_W * 0.95, HONEYCOMB_ROW_H = HEX_H / 0.95;
const HONEYCOMB_LAYOUT = [
  { col: 0, row: 0 }, { col: 0, row: 1 }, { col: 0, row: 2 },
  { col: 1, row: 0 }, { col: 1, row: 1 }, { col: -1, row: 0.5 },
  { col: 2, row: 0 }, { col: 2, row: 1 },
];
const HEX_COLORS = ['#005B66','#0B7078', '#D62828', '#00464f'];
const DECOR_HEXES = [
  { left: '-6%', top: '4%', size: 170 },
  { left: '1%', top: '58%', size: 130 },
  { left: '-3%', top: '84%', size: 100 },
  { left: '97%', top: '2%', size: 150 },
  { left: '90%', top: '48%', size: 190 },
  { left: '98%', top: '86%', size: 110 },
];

// Split desktop honeycomb — two small clusters (one per side of the
// content). Dialed size back down (150 -> 115) and dropped the extra outer
// ring of plain hexes — at the bigger size each cluster was ~577px wide,
// which combined with plain "1fr" side columns was forcing those grid
// tracks to reserve that much space regardless of the fr ratio, squeezing
// the middle column down to almost nothing. Combined with the grid template
// fix below (fixed-width caps instead of 1fr), this keeps things contained.
const MINI_HEX_W = 115, MINI_HEX_H = 115, MINI_COL_W = MINI_HEX_W * 0.95, MINI_ROW_H = MINI_HEX_H / 0.95;
const PLAIN_HEX_COLORS = ['rgba(0,91,102,0.32)', 'rgba(11,112,120,0.28)', 'rgba(214,40,40,0.24)', 'rgba(0,70,79,0.28)'];

const LEFT_CLUSTER_LAYOUT = [
  { col: -1, row: 0.5, plain: true }, { col: -1, row: 1.5, plain: true },
  { col: 0, row: 0 }, { col: 0, row: 1 },
  { col: 1, row: 0.5 }, { col: 1, row: 1.5 },
];
const RIGHT_CLUSTER_LAYOUT = [
  { col: -1, row: 0 }, { col: -1, row: 1 },
  { col: 0, row: 0.5 }, { col: 0, row: 1.5 },
  { col: 1, row: 0.5, plain: true }, { col: 1, row: 1.5, plain: true },
];

// Mobile-only honeycomb — same tessellating idea as the desktop one, sized
// down and laid out as a symmetric 3–2–3 column pattern (instead of one hex
// sitting a full column further left than the container's own width, which
// is what made the desktop version impossible to just scale down for a
// phone). Columns are packed tighter (0.78 vs desktop's 0.95) so the hexes
// actually touch like a real honeycomb instead of floating with visible gaps.
const M_HEX_W = 95, M_HEX_H = 95, M_COL_W = M_HEX_W * 0.78, M_ROW_H = M_HEX_W;
const M_HONEYCOMB_LAYOUT = [
  { col: 0, row: 0 }, { col: 0, row: 1 }, { col: 3, row: 0.5 },
  { col: 1, row: 0.5 }, { col: 1, row: 1.5 },
  { col: 2, row: 0 }, { col: 2, row: 1 }, { col: -1, row: 0.5 },
];

const IMPACT_POSITIONS = [
  { left: '5%', top: '-20%' },  
  { left: '24%', top: '25%' },  
  { left: '76%', top: '25%' },  
  { left: '95%', top: '-20%' }, 
];

const ImpactCard: React.FC<{ item: typeof impactNodes[0]; index: number; pos: { left: string; top: string }; isMobile: boolean }> = ({ item, index, pos, isMobile }) => {
  const [hovered, setHovered] = useState(false);
  const captionVisible = isMobile || hovered;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.7 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.15 + index * 0.14, duration: 0.55, ease: 'backOut' }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      className="impact-card"
      style={{
        position: 'absolute', left: pos.left, top: pos.top, x: '-50%', y: '-50%',
        zIndex: 1, width: isMobile ? 260 : 480, display: 'flex', flexDirection: 'column', alignItems: 'center',
      }}
    >
      <div className={isMobile ? undefined : 'float'} style={{ animationDelay: `${index * 0.45}s` }}>
        <div style={{
          width: item.size, height: item.size, borderRadius: '50%', overflow: 'hidden',
          border: '6px solid #fff', boxShadow: '0 24px 48px rgba(0,20,22,0.28)',
          transform: 'translateZ(0)', WebkitBackfaceVisibility: 'hidden',
        }}>
<SafeImage src={item.img} alt={item.desc} style={{ objectFit: 'contain', background: '#f7f9fa' }} />
        </div>
      </div>
      <motion.div
        animate={{ opacity: captionVisible ? 1 : 0, y: captionVisible ? 0 : 6 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        style={{ marginTop: 10, textAlign: 'center', pointerEvents: 'none' }}
      >
        <p style={{ fontSize: 15, fontFamily: 'Poppins, sans-serif', color: '#374151', lineHeight: 1.5, background: '#fff', padding: '6px 10px', borderRadius: 10, boxShadow: '0 8px 20px rgba(0,20,22,0.12)' }}>
          {item.desc}
        </p>
      </motion.div>
    </motion.div>
  );
};

const ImpactFloatRow: React.FC<{ items: typeof impactNodes; isMobile: boolean }> = ({ items, isMobile }) => (
  <div className="impact-row" style={{ position: 'relative', minHeight: 460 }}>
    <div className="impact-dna-bleed" style={{ position: 'absolute', top: -100, bottom: 50, left: '50%', width: '100vw', transform: 'translateX(-50%)', zIndex: 0 }}>
      <DnaParticles disabled={isMobile} />
    </div>
    {items.map((it, i) => (
      <ImpactCard key={i} item={it} index={i} pos={IMPACT_POSITIONS[i] || IMPACT_POSITIONS[IMPACT_POSITIONS.length - 1]} isMobile={isMobile} />
    ))}
    <style>{`
      @media(max-width:768px){
        .impact-row{ min-height: auto!important; padding-bottom: 40px; }
        .impact-card{ position: static!important; transform: none!important; margin: 0 auto 32px!important; }
        .impact-dna-bleed{ height: 260px!important; }
      }
    `}</style>
  </div>
);

const StatCounter: React.FC<{ value: string }> = ({ value }) => {
  const digits = value.match(/[\d,]+/)?.[0] ?? '0';
  const target = parseInt(digits.replace(/,/g, ''), 10);
  const suffix = value.replace(/[\d,]+/, '');
  const [count, setCount] = useState(0);

  useEffect(() => {
    let raf = 0;
    const duration = 1600;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(target * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  return <>{count.toLocaleString('en-IN')}{suffix}</>;
};



const FOUNDER_NAME = 'Dr. K R Premchand';
const FOUNDER_ROLE = 'Managing Director';

const Home: React.FC = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const [activeHex, setActiveHex] = useState(0);
  const [hexHovering, setHexHovering] = useState(false);
  useEffect(() => {
    if (hexHovering) return; 
    const id = setInterval(() => setActiveHex(prev => (prev + 1) % whyChoose.length), 5000);
    return () => clearInterval(id);
  }, [hexHovering]);

  const renderHexCluster = (layout: typeof LEFT_CLUSTER_LAYOUT, items: typeof whyChoose, indexOffset: number) => {
    let realIdx = 0;
    return (
      <div style={{ position: 'relative', width: MINI_COL_W * 2 + MINI_HEX_W, height: MINI_ROW_H * 1.5 + MINI_HEX_H, margin: '0 auto', minWidth: 0 }}>
        {layout.map((pos, i) => {
          const x = (pos.col + 1) * MINI_COL_W;
          const y = pos.row * MINI_ROW_H;
          if (pos.plain) {
            return (
              <motion.div
                key={`plain-${i}`}
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4 + i, repeat: Infinity, ease: 'easeInOut', delay: i * 0.3 }}
                style={{ position: 'absolute', left: x, top: y, width: MINI_HEX_W, height: MINI_HEX_H, clipPath: HEX_CLIP, background: PLAIN_HEX_COLORS[i % PLAIN_HEX_COLORS.length], pointerEvents: 'none' }}
              />
            );
          }
          const w = items[realIdx];
          const globalIdx = indexOffset + realIdx;
          realIdx++;
          return (
            <motion.div
              key={w.title}
              initial={{ opacity: 0, scale: 0.6 }} whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => { setActiveHex(globalIdx); setHexHovering(true); }}
              onMouseLeave={() => setHexHovering(false)}
              style={{
                position: 'absolute', left: x, top: y, width: MINI_HEX_W, height: MINI_HEX_H, clipPath: HEX_CLIP,
                background: HEX_COLORS[globalIdx % HEX_COLORS.length],
                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                boxShadow: activeHex === globalIdx ? '0 0 0 3px rgba(0,91,102,0.35), 0 12px 26px rgba(0,20,22,0.22)' : '0 10px 22px rgba(0,20,22,0.16)',
                transition: 'box-shadow 0.3s ease',
              }}
            >
              <w.Icon size={20} color="#fff" />
            </motion.div>
          );
        })}
      </div>
    );
  };
  
  const journeyPinRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: journeyProgressRaw } = useScroll({ target: journeyPinRef, offset: ['start 15%', 'end end'] });
  // Spring-smoothed so every value derived from scroll (text/timeline width,
  // font size, reveal index, line draw) eases gracefully instead of tracking
  // the scrollbar 1:1 — that 1:1 tracking is what made the text-to-left
  // transition feel abrupt before.
  const journeyProgress = useSpring(journeyProgressRaw, { stiffness: 90, damping: 28, restDelta: 0.001 });
  const [journeyRevealed, setJourneyRevealed] = useState(0);
  const journeyRawIndex = useTransform(journeyProgress, [0.1, 0.9], [0, journey.length]);
  
  useMotionValueEvent(journeyRawIndex, 'change', (v) => {
    setJourneyRevealed(Math.min(journey.length, Math.max(0, Math.round(v))));
  });
  
  const journeyDraw = useTransform(journeyProgress, [0.05, 0.95], [0, 1]);
  
  // Cinematic transition: Start visually wide and centered, then glide gracefully to the left.
  // Widened from the original 0–0.15 window to 0–0.35 so it plays out over a
  // longer, gentler stretch of scroll instead of snapping across in a flash.
  const journeyTextWidth = useTransform(journeyProgress, [0, 0.35], ['100%', '25%']);
  const journeyTimelineWidth = useTransform(journeyProgress, [0, 0.35], ['0%', '75%']);
  const journeyTimelineOpacity = useTransform(journeyProgress, [0.05, 0.35], [0, 1]);
  const journeyFontSize = useTransform(journeyProgress, [0, 0.35], ['clamp(36px, 5vw, 64px)', 'clamp(28px, 4vw, 54px)']);
  const journeySubtitleOpacity = useTransform(journeyProgress, [0, 0.1], [1, 0]);
  const journeySubtitleHeight = useTransform(journeyProgress, [0, 0.1], ['auto', 0]);

  const [isTimelineActive, setIsTimelineActive] = useState(false);
  useMotionValueEvent(journeyProgress, 'change', (v) => setIsTimelineActive(v > 0.05));

  return (
    <>
      <section
        ref={heroRef}
        className="grain"
        style={{
          minHeight: '94vh', background: '#ffffff',
          display: 'flex', alignItems: 'center', paddingTop: 0, paddingBottom: -20, position: 'relative', overflow: 'hidden',
        }}>
        
        {/* Continuous, luxurious cinematic ambient light movement in background */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
          
          {/* --- NEW SUBTLE ATMOSPHERIC BACKGROUND IMAGE (INTERNET URL) --- */}
          {/* This sits absolutely at the back. Visibility tuned to be extremely subtle yet present. */}
          <div style={{ position: 'absolute', inset: 0, opacity: 0.15, filter: 'grayscale(100%) blur(2px)' }}>
            <img src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?q=80&w=2000&auto=format&fit=crop" alt="Premium Healthcare Atmosphere" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'left center' }} />
          </div>
          
          {/* Strong white overlays to blend and fade the image naturally */}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.95) 70%, #ffffff 100%)' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(255,255,255,0.3) 0%, #ffffff 95%)' }} />

          {/* Base gradient */}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, rgba(240,253,250,0.8) 0%, rgba(248,250,252,0.9) 45%, rgba(255,241,242,0.85) 100%)' }} />
          
          {!isMobile && (
            <>
              <motion.div animate={{ x: [0, 30, 0], y: [0, 20, 0], opacity: [0.3, 0.6, 0.3], scale: [1, 1.1, 1] }} transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }} style={{ position: 'absolute', top: '-10%', left: '-5%', width: '50vw', height: '50vw', background: 'radial-gradient(circle, rgba(0,91,102,0.06) 0%, transparent 70%)', filter: 'blur(80px)' }} />
              <motion.div animate={{ x: [0, -20, 0], y: [0, -30, 0], opacity: [0.2, 0.5, 0.2], scale: [1, 1.2, 1] }} transition={{ duration: 15, repeat: Infinity, ease: "easeInOut", delay: 2 }} style={{ position: 'absolute', bottom: '-15%', right: '-5%', width: '60vw', height: '60vw', background: 'radial-gradient(circle, rgba(214,40,40,0.05) 0%, transparent 70%)', filter: 'blur(80px)' }} />
            </>
          )}
        </div>

        {/* Ambient floating particles — skipped on mobile */}
        {!isMobile && (
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }}>
             {[...Array(8)].map((_, i) => (
               <motion.div key={i} animate={{ y: [0, -40, 0], x: [0, 20, 0], opacity: [0, 0.3, 0] }} transition={{ duration: 8 + i * 2, repeat: Infinity, ease: "easeInOut", delay: i * 1.5 }} style={{ position: 'absolute', left: `${10 + i * 10}%`, top: `${30 + (i % 4) * 15}%`, width: Math.random() * 4 + 2, height: Math.random() * 4 + 2, borderRadius: '50%', background: '#005B66' }} />
             ))}
          </div>
        )}

        <div style={{
          position: 'absolute', inset: 0, zIndex: 1,
          backgroundImage: 'linear-gradient(rgba(0,91,102,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,91,102,0.04) 1px, transparent 1px)',
          backgroundSize: '46px 46px',
          WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, #000 40%, transparent 80%)',
          maskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, #000 40%, transparent 80%)',
        }} />

        <div className="container" style={{ position: 'relative', zIndex: 2, paddingTop: 'clamp(60px, 6vh, 90px)' }}>
          <div className="hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.15fr', gap: 40, alignItems: 'center', minHeight: '76vh' }}>

            <div className="hero-text-col" style={{ position: 'relative', zIndex: 2, paddingBottom: 40 }}>
              <div style={{ position: 'relative', zIndex: 1 }}>
                
                {/* Two explicit lines, each word-by-word blur-revealed. Switched
                    from whileInView to animate — this section is always the
                    first thing on screen at load, so it doesn't need scroll-
                    triggered reveal, and on some mobile viewports the
                    whileInView intersection check wasn't firing reliably,
                    which is what was leaving "One Reach." stuck invisible. */}
                <motion.h1 variants={heroTextContainer} initial="hidden" animate="show" style={{ fontFamily: 'Poppins, sans-serif', fontSize: 'clamp(32px, 4vw, 54px)', fontWeight: 800, color: '#1a1a1a', lineHeight: 1.12, marginBottom: 20 }}>
                  <div>
                    {['One', 'Reach.'].map((word, i) => (
                      <motion.span key={`l1-${i}`} variants={heroWord} style={{ display: 'inline-block', marginRight: '0.25em' }}>
                        {word}
                      </motion.span>
                    ))}
                  </div>
                  <div>
                    {['Endless', 'Care.'].map((word, i) => (
                      <motion.span key={`l2-${i}`} variants={heroWord} style={{ display: 'inline-block', marginRight: '0.25em' }}>
                        <span className="gradient-text">{word}</span>
                      </motion.span>
                    ))}
                  </div>
                </motion.h1>

                <motion.p initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }} style={{ fontSize: 18, color: '#1a1a1a', lineHeight: 1.8, marginBottom: 30 }}>
                  A unified group of healthcare and community services dedicated to world-class care, compassion and innovation for a healthier tomorrow spanning 9+ specialised verticals.
                </motion.p>

                {/* Shuffled Card Animation for Metric Glass Cards */}
                <motion.div className="hero-metrics" initial="hidden" animate="show" variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.15, delayChildren: 0.8 } } }} style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 32 }}>
                  {[
                    { value: '10+', label: 'Specialised Verticals', icon: Building2 },
                    { value: '2,34,800+', label: 'Lives Served', icon: Users },
                    { value: '11+', label: 'Expert Professionals', icon: Stethoscope },
                    { value: '15+', label: 'Years of Trust', icon: Clock3 },
                  ].map((s, i) => (
                    <motion.div key={s.label} variants={cardShuffle} style={{ position: 'relative' }}>
                      <motion.div 
                        animate={isMobile ? undefined : { y: [0, -4, 0] }} transition={isMobile ? undefined : { duration: 4, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
                        whileHover={{ y: -6, scale: 1.02, boxShadow: '0 15px 40px rgba(0,91,102,0.08)' }} 
                        style={{ background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,1)', padding: '16px', borderRadius: '16px', boxShadow: '0 8px 24px rgba(0,0,0,0.03)', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', transition: 'all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1)' }}
                      >
                        <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#e8f4f5', display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
                          <s.icon size={20} color="#005B66" />
                        </div>
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: 18, color: '#1a1a1a', lineHeight: 1.1 }}><StatCounter value={s.value} /></div>
                          <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 500, marginTop: 2 }}>{s.label}</div>
                        </div>
                      </motion.div>
                    </motion.div>
                  ))}
                </motion.div>

                <motion.div className="hero-cta-row" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.9 }} style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  <Link to="/groups" className="btn-primary shine" style={{ transition: 'all 0.3s' }} onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,91,102,0.3)'} onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}>Explore Our Verticals <ArrowRight size={16} /></Link>
                  <Link to="/contact" className="btn-secondary" style={{ transition: 'all 0.3s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>Contact Us</Link>
                </motion.div>
              </div>
            </div>

            <div className="hero-image-col" style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div aria-hidden style={{
                  position: 'absolute', left: '40%', top: '20%', transform: 'translate(-50%,-50%)',
                  fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: 'clamp(46px,8vw,96px)',
                  color: 'transparent', WebkitTextStroke: '1.5px rgba(0,91,102,0.12)', letterSpacing: '-2px',
                  whiteSpace: 'nowrap', zIndex: 0, pointerEvents: 'none', textTransform: 'uppercase',
                }}>Reach GROUPS</div>

                {/* Editorial Integration of Premchand — no longer floats
                    continuously; sits still so it doesn't compete with the
                    other motion happening around it. Width cap raised from
                    580 to 680 so the photo actually fills its (now wider,
                    1.15fr) column instead of leaving empty space beside it. */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: 'easeOut' }}
                  style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 680 }}
                >
                  <div>
                    
                    {/* Breathing Ambient Glow behind Portrait — skipped on mobile */}
                    {!isMobile && (
                      <motion.div animate={{ scale: [1, 1.1, 1], opacity: [0.4, 0.7, 0.4] }} transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }} style={{ position: 'absolute', inset: '-10%', background: 'radial-gradient(circle, rgba(0,91,102,0.2) 0%, transparent 60%)', filter: 'blur(30px)', zIndex: 0 }} />
                    )}
                    
                    <SafeImage
                      src={stock.founder}
                      alt="Founder of Reach Groups"
                      style={{
                        position: 'relative', zIndex: 1, objectFit: 'contain', objectPosition: 'bottom',
                        WebkitMaskImage: isMobile ? undefined : 'linear-gradient(90deg, #000 78%, transparent 100%)',
                        maskImage: isMobile ? undefined : 'linear-gradient(90deg, #000 78%, transparent 100%)',
                      }}
                    />
                    
                    <motion.div
                      initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.6 }}
                      style={{ position: 'absolute', left: '0', bottom: '15%', zIndex: 2, display: 'flex', flexDirection: 'column', gap: 4 }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <motion.div animate={{ height: [10, 40] }} transition={{ duration: 1, delay: 0.8, ease: "easeOut" }} style={{ width: 4, background: '#005B66', borderRadius: '4px' }} />
                        <div style={{ backgroundColor: '#ffffff', borderRadius: 12, padding: '6px 12px', boxShadow: '0 8px 24px rgba(0,20,22,0.08)' }}>
                          <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: 18, color: '#1a1a1a', textShadow: '0 2px 10px rgba(255,255,255,0.9)' }}>{FOUNDER_NAME}</div>
                          <div style={{ fontSize: 12.5, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', textShadow: '0 2px 10px rgba(255,255,255,0.9)' }}>{FOUNDER_ROLE}</div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Verticals marquee — this had gone missing entirely from the file,
              restored here. Full-viewport-width strip, each pill opens that
              vertical's own site. */}
          <div className="marquee-wrap" style={{
            padding: '24px 0', overflow: 'hidden', zIndex: 3, position: 'relative',
            left: '50%', width: '100vw', marginLeft: '-50vw',
          }}>
            <div className="marquee-track" style={{ animationDuration: '32s' }}>
              {[...Array(2)].map((_, dup) => (
                <div key={dup} style={{ display: 'flex', gap: 18, paddingRight: 18, paddingLeft: dup === 0 ? 'clamp(16px, 4vw, 64px)' : 0 }}>
                  {verticals.map(v => {
                    const Icon = verticalIcons[v.id];
                    return (
                      <a key={`${v.id}-${dup}`} href={urlForVertical(v)} target="_blank" rel="noopener noreferrer" style={{
                        display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap',
                        background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(10px)', border: '1px solid rgba(0,91,102,0.08)', borderRadius: 100, padding: '11px 22px 11px 12px', transition: 'all 0.3s', textDecoration: 'none',
                      }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = 'rgba(0,91,102,0.2)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,91,102,0.06)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'rgba(0,91,102,0.08)'; e.currentTarget.style.boxShadow = 'none'; }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#e8f4f5', color: '#005B66', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {Icon && <Icon size={16} />}
                        </div>
                        <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: 14, color: '#1a1a1a' }}>{v.name}</span>
                      </a>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
        <style>{`
          @media(max-width:1024px){
            .hero-grid{grid-template-columns:1fr!important; text-align:center;}
            .hero-image-col{order:-1;}
            .hero-text-col{order:2;}
            .hero-cta-row{flex-wrap:nowrap!important;}
            .hero-cta-row a{flex:1; justify-content:center; font-size:13px; padding:11px 10px!important;}
          }
          @media(max-width:480px){
            .hero-metrics{grid-template-columns:1fr!important;}
          }
        `}</style>
      </section>
      {/* OUR JOURNEY — Smooth, wide-to-left transition, no dead scroll */}
      <section style={{ padding: '80px 20px', background: '#F7F9FA', position: 'relative' }}>
        <div className="container" style={{ maxWidth: '1600px' }}>
          {isMobile ? (
            /* Mobile: a plain stacked timeline instead of the pinned/scroll-
               linked wave. The desktop version's reveal progress is computed
               from the pinned container's scroll distance (160vh) — once CSS
               collapses that same container to `height: auto` on mobile (so
               it doesn't scroll-jack a phone), the progress math no longer
               matches the actual scrollable distance, which is what made the
               circles seem to reveal out of order/in reverse when swiping.
               A plain whileInView reveal can't go in reverse, and the photo
               circles are drawn at a fixed, much bigger size instead of
               scaling down with a wide desktop-oriented SVG viewBox. */
            <div style={{ padding: '10px 0' }}>
              <h2 className="section-title" style={{ textAlign: 'center' }}>
                A Legacy of <span style={{ color: '#005B66' }}>Care,</span> Charted Over Time.
              </h2>
              <p style={{ color: '#6B7280', fontSize: 15, lineHeight: 1.7, textAlign: 'center', marginBottom: 36 }}>
                From clinics to diagnostics, hospitals to home care growth built one milestone at a time.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                {journey.map((j, i) => (
                  <React.Fragment key={j.year}>
                    <motion.div
                      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }}
                      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', transform: `translateX(${i % 2 === 0 ? -26 : 26}px)` }}
                    >
                      <div style={{ width: 160, height: 160, borderRadius: '50%', overflow: 'hidden', border: '6px solid #fff', boxShadow: '0 14px 34px rgba(0,20,22,0.14)', marginBottom: 14, transform: 'translateZ(0)', WebkitBackfaceVisibility: 'hidden' }}>
                        <SafeImage src={j.img} alt={j.label} style={{ objectFit: 'contain', background: '#f7f9fa' }} />
                      </div>
                      <div style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: 22, color: '#005B66', marginBottom: 8 }}>{j.year}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid rgba(0,91,102,0.12)', borderRadius: 16, padding: '12px 18px', boxShadow: '0 12px 24px rgba(0,20,22,0.06)', maxWidth: 280 }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e8f4f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <j.icon size={18} color="#005B66" />
                        </div>
                        <span style={{ fontSize: 14, color: '#1a1a1a', fontWeight: 700, lineHeight: 1.3, textAlign: 'left' }}>{j.label}</span>
                      </div>
                    </motion.div>
                    {i < journey.length - 1 && (
                      <div style={{ position: 'relative', width: 4, height: 40, borderRadius: 2, background: 'rgba(0,91,102,0.15)', overflow: 'hidden' }}>
                        <motion.div
                          animate={{ top: ['-14px', '40px'] }}
                          transition={{ duration: 1.3, repeat: Infinity, ease: 'linear', delay: i * 0.25 }}
                          style={{ position: 'absolute', left: '50%', width: 10, height: 14, borderRadius: 5, background: 'linear-gradient(180deg, #005B66, #D62828)', transform: 'translateX(-50%)' }}
                        />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Values — explicit 2-top / heart-middle / 2-bottom layout
                  instead of letting 5 items wrap unevenly across a 2-column
                  grid (which left the last item alone on its own row). */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 28, marginTop: 48, maxWidth: 420, margin: '48px auto 0' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, width: '100%' }}>
                  <ValueColumn item={journey[0]} Icon={valueIcons[0]} index={0} />
                  <ValueColumn item={journey[1]} Icon={valueIcons[1]} index={1} />
                </div>

                <div style={{ textAlign: 'center' }}>
                  <div style={{ position: 'relative', width: 56, height: 56, margin: '0 auto 16px' }}>
                    <motion.div
                      style={{ position: 'relative', zIndex: 1, width: 56, height: 56, borderRadius: '50%', background: '#fdeaea', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      animate={{ scale: [1, 1.18, 1, 1.1, 1] }}
                      transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.5 }}
                    >
                      <Heart size={24} color="#D62828" fill="#D62828" />
                    </motion.div>
                  </div>
                  <p style={{ fontStyle: 'italic', color: '#374151', fontSize: 14.5, lineHeight: 1.7, margin: 0 }}>
                    Delivering care that goes beyond expectations.
                  </p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, width: '100%' }}>
                  <ValueColumn item={journey[2]} Icon={valueIcons[2]} index={2} />
                  <ValueColumn item={journey[3]} Icon={valueIcons[3]} index={3} />

                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Greatly reduced empty scrolling space (160vh ensures smooth transition without dead space) */}
              <div ref={journeyPinRef} className="journey-pin" style={{ position: 'relative', height: `160vh`, marginBottom: 30 }}>
                
                <div className="journey-row" style={{ position: 'sticky', top: '20vh', display: 'flex', alignItems: 'center', width: '100%', overflow: 'hidden' }}>
                  
                  {/* The Heading Container: Shrinks from 100% to 25%, causing words to elegantly stack on the left */}
                  <motion.div style={{ width: journeyTextWidth, padding: '0 2vw' }}>
                    <motion.h2 
                      className="section-title" 
                      style={{ 
                        fontSize: journeyFontSize, 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        justifyContent: isTimelineActive ? 'flex-start' : 'center', 
                        alignItems: 'center',
                        gap: '0.2em', 
                        lineHeight: 1.1,
                        margin: 0,
                        transition: 'justify-content 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
                      }}
                    >
                      {/* Each word gracefully reflows as the container shrinks */}
                      {['A', 'Legacy', 'of', 'Care,', 'Charted', 'Over', 'Time.'].map((word, i) => (
                        <span key={i} style={{ color: word === 'Care,' ? '#005B66' : 'inherit' }}>{word}</span>
                      ))}
                    </motion.h2>
                    
                    {/* Subtitle explicitly fades out as the timeline engages to keep the left column clean */}
                    <motion.div style={{ opacity: journeySubtitleOpacity, height: journeySubtitleHeight, overflow: 'hidden' }}>
                      <p style={{ color: '#6B7280', fontSize: 16, lineHeight: 1.8, marginTop: 16, textAlign: 'center' }}>
                        From clinics to diagnostics, hospitals to home care growth built one milestone at a time.
                      </p>
                    </motion.div>
                  </motion.div>

                  {/* The Timeline Container: Expands from 0% to 75% wide, dominating the section */}
                  <motion.div style={{ width: journeyTimelineWidth, opacity: journeyTimelineOpacity, overflow: 'visible', paddingRight: '2vw' }}>
                    <JourneyWave items={journey} revealed={journeyRevealed} drawProgress={journeyDraw} reduceMotion={isMobile} />
                  </motion.div>
                  
                </div>
              </div>

              <div className="journey-values" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, alignItems: 'center', maxWidth: '1200px', margin: '0 auto' }}>
                {journey.map((j, i) => {
                  if (i === 1) {
                    return (
                      <React.Fragment key="quote-slot">
                        <ValueColumn item={j} Icon={valueIcons[i]} index={i} />
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                          style={{ borderRadius: 18, padding: '30px 22px', textAlign: 'center', position: 'relative' }}
                        >
                          <div style={{ position: 'relative', width: 56, height: 56, margin: '0 auto 16px' }}>
                            {[0, 1].map(ring => (
                              <motion.div
                                key={ring}
                                style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid #D62828' }}
                                animate={{ scale: [1, 1.9], opacity: [0.55, 0] }}
                                transition={{ duration: 1.7, repeat: Infinity, ease: 'easeOut', delay: ring * 0.85 }}
                              />
                            ))}
                            <motion.div
                              style={{ position: 'relative', zIndex: 1, width: 56, height: 56, borderRadius: '50%', background: '#fdeaea', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              animate={{ scale: [1, 1.18, 1, 1.1, 1] }}
                              transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.5 }}
                            >
                              <Heart size={24} color="#D62828" fill="#D62828" />
                            </motion.div>
                          </div>
                          <p style={{ fontStyle: 'italic', color: '#374151', fontSize: 14.5, lineHeight: 1.7 }}>
                            Delivering care that goes beyond expectations.
                          </p>
                        </motion.div>
                      </React.Fragment>
                    );
                  }
                  return <ValueColumn key={j.title} item={j} Icon={valueIcons[i]} index={i} />;
                })}
              </div>
            </>
          )}
        </div>
        <style>{`
          @media(max-width:900px){
            .journey-pin{height:auto!important;}
            .journey-row{flex-direction: column!important; position:static!important;}
          }
        `}</style>
      </section>

      {/* SWAPPED: COMMUNITY IMPACT FIRST */}
      <section style={{ padding: '10px 0', bottom:10 ,background: '#F7F9FA', position: 'relative', overflow: 'hidden' }}>
        <div className="container" style={{ position: 'relative' }}>
          <Reveal>
            <div style={{ textAlign: 'center', maxWidth: 680, margin: '0 auto 48px' }}>
              <h2 className="section-title" style={{ margin: '10px 0 16px' }}>
                A Unified Vision for a <span style={{ color: '#005B66' }}>Healthier Community.</span>
              </h2>
              <p className="section-subtitle" style={{ margin: '0 auto' }}>
                From our clinical roots to a diversified health ecosystem, Reach Groups has grown with a central purpose: world-class care and community welfare. Every vertical is an extension of our vision to serve and innovate.
              </p>
            </div>
          </Reveal>

          <ImpactFloatRow items={impactNodes} isMobile={isMobile} />

        </div>
        <style>{`@media(max-width:768px){section .container>div[style*="repeat(3, 1fr)"]{grid-template-columns:1fr!important}}`}</style>
      </section>

      {/* SWAPPED: OUR ADVANTAGES SECOND */}
      <section style={{ padding: '36px 0', background: '#F7F9FA', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: '50%', width: '100vw', transform: 'translateX(-50%)', zIndex: 0, pointerEvents: 'none' }} aria-hidden>
          {!isMobile && DECOR_HEXES.map((d, i) => (
            <motion.div
              key={i}
              animate={{ y: [0, -16, 0], rotate: [0, 2, -2, 0] }}
              transition={{ duration: 6 + i * 0.8, repeat: Infinity, ease: 'easeInOut', delay: i * 0.4 }}
              style={{ position: 'absolute', left: d.left, top: d.top, width: d.size, height: d.size, clipPath: HEX_CLIP, background: 'rgba(0,91,102,0.05)' }}
            >
              <div style={{ position: 'absolute', inset: 6, clipPath: HEX_CLIP, background: '#F7F9FA' }} />
            </motion.div>
          ))}
        </div>

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="advantage-row" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 340px) 1fr minmax(0, 340px)', gap: 24, alignItems: 'center' }}>
            {isMobile ? (
              <>
                {/* Mobile: a proper tessellating honeycomb — 3 columns (3, 2, 3
                   hexes), packed tight enough that they actually touch like a
                   real honeycomb, sized and positioned to fit a phone without
                   the desktop layout's overflow problem. */}
                <div style={{ position: 'relative', width: M_COL_W * 2 + M_HEX_W, height: M_ROW_H * 2 + M_HEX_H, margin: '0 auto', minWidth: 0 }}>
                  {whyChoose.map((w, i) => {
                    const { col, row } = M_HONEYCOMB_LAYOUT[i];
                    const x = col * M_COL_W;
                    const y = row * M_ROW_H;
                    return (
                      <div
                        key={w.title}
                        onClick={() => setActiveHex(i)}
                        style={{
                          position: 'absolute', left: x, top: y, width: M_HEX_W, height: M_HEX_H, clipPath: HEX_CLIP,
                          background: HEX_COLORS[i % HEX_COLORS.length],
                          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                          boxShadow: activeHex === i ? '0 0 0 3px rgba(0,91,102,0.35), 0 10px 22px rgba(0,20,22,0.22)' : '0 8px 18px rgba(0,20,22,0.15)',
                          transition: 'box-shadow 0.3s ease',
                        }}
                      >
                        <w.Icon size={20} color="#fff" />
                      </div>
                    );
                  })}
                </div>

                {/* This heading/description/detail-panel block was missing
                    from the mobile branch entirely — it only rendered the
                    hex grid, which is why the text disappeared on phones. */}
                <div>
                  <h2 className="section-title" style={{ marginBottom: 16, textAlign: 'center' }}>Why Choose Reach Groups ?</h2>
                  <p style={{ color: '#6B7280', fontSize: 15, lineHeight: 1.8, marginBottom: 22, textAlign: 'center' }}>
                    From advanced diagnostics to home healthcare, every vertical under Reach Groups is built around one goal dependable, compassionate care across Chennai.
                  </p>
                  <div style={{ minHeight: 84 }}>
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeHex}
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="sheen-panel" style={{ display: 'flex', gap: 14, alignItems: 'flex-start', borderRadius: 16, padding: '30px 18px' }}
                      >
                        <div className="icon-badge" style={{ width: 44, height: 44, background: '#e8f4f5', color: '#005B66', justifyContent: 'center', flexShrink: 0 }}>
                          {React.createElement(whyChoose[activeHex].Icon, { size: 22 })}
                        </div>
                        <div>
                          <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: 15, color: '#1a1a1a', marginBottom: 3 }}>{whyChoose[activeHex].title}</div>
                          <div style={{ fontSize: 13.5, color: '#6B7280', lineHeight: 1.6 }}>{whyChoose[activeHex].desc}</div>
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>
              </>
            ) : (
              <>
                {renderHexCluster(LEFT_CLUSTER_LAYOUT, whyChoose.slice(0, 4), 0)}
                <div>
                  <h2 className="section-title" style={{ marginBottom: 16, textAlign: 'center' }}>Why Choose Reach Groups ?</h2>
                  <p style={{ color: '#6B7280', fontSize: 15, lineHeight: 1.8, marginBottom: 22, textAlign: 'center' }}>
                    From advanced diagnostics to home healthcare, every vertical under Reach Groups is built around one goal dependable, compassionate care across Chennai.
                  </p>
                  <div style={{ minHeight: 84 }}>
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeHex}
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="sheen-panel" style={{ display: 'flex', gap: 14, alignItems: 'flex-start', borderRadius: 16, padding: '30px 18px' }}
                      >
                        <div className="icon-badge" style={{ width: 44, height: 44, background: '#e8f4f5', color: '#005B66', justifyContent: 'center', flexShrink: 0 }}>
                          {React.createElement(whyChoose[activeHex].Icon, { size: 22 })}
                        </div>
                        <div>
                          <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: 15, color: '#1a1a1a', marginBottom: 3 }}>{whyChoose[activeHex].title}</div>
                          <div style={{ fontSize: 13.5, color: '#6B7280', lineHeight: 1.6 }}>{whyChoose[activeHex].desc}</div>
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>

                {renderHexCluster(RIGHT_CLUSTER_LAYOUT, whyChoose.slice(4, 8), 4)}
                </>
            )}
          </div>
        </div>
        <style>{`
          @media(max-width:900px){
            .advantage-row{ grid-template-columns: 1fr!important; }
          }
        `}</style>
      </section>

      {/* CTA */}
      <section style={{ top:0, padding: '26px 0', position: 'relative', overflow: 'hidden', background: 'linear-gradient(160deg, #eef8f90b 0%, #f8fbfb 45%, #fdf1f1 100%)' }}>
        <div className="blob float" style={{ top: -100, left: '38%', width: 360, height: 360, background: 'radial-gradient(circle, rgba(0,91,102,0.06) 0%, transparent 70%)' }} />

        {!isMobile && (
          <>
            <motion.div
              initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}
              style={{
                position: 'absolute', left: 0, top: 0, bottom: 0, width: 'clamp(180px, 20vw, 320px)', zIndex: 0,
                WebkitMaskImage: 'linear-gradient(90deg, #000 55%, transparent 100%)',
                maskImage: 'linear-gradient(90deg, #000 55%, transparent 100%)',
              }}
            >
              <SafeImage src={stock.nurseElder} alt="Families cared for by Reach Groups" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              style={{
                position: 'absolute', right: 0, top: 0, bottom: 0, width: 'clamp(180px, 20vw, 320px)', zIndex: 0,
                WebkitMaskImage: 'linear-gradient(270deg, #000 55%, transparent 100%)',
                maskImage: 'linear-gradient(270deg, #000 55%, transparent 100%)',
              }}
            >
              <SafeImage src={stock.campus} alt="Reach Groups facilities" />
            </motion.div>
          </>
        )}

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="cta-row" style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 40, alignItems: 'center', maxWidth: 980, margin: '0 auto', padding: '0 clamp(0px, 14vw, 220px)' }}>
            <Reveal direction="left">
              <h2 style={{ fontFamily: 'Poppins, sans-serif', fontSize: 'clamp(28px, 3.6vw, 42px)', fontWeight: 800, color: '#1a1a1a', lineHeight: 1.25, marginBottom: 4 }}>
                Ready to Start Your<br />Health Journey?
                <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 10, verticalAlign: 'middle' }}>
                  <motion.svg width="46" height="26" viewBox="0 0 46 26" style={{ display: 'block' }}>
                    <motion.path
                      d="M2 6 C 14 2, 22 22, 38 10" fill="none" stroke="#005B66" strokeWidth={2} strokeLinecap="round"
                      initial={{ pathLength: 0, opacity: 0 }} whileInView={{ pathLength: 1, opacity: 1 }} transition={{ duration: 0.9, delay: 0.4 }}
                    />
                  </motion.svg>
                  <motion.span
                    style={{ position: 'absolute', right: -4, top: -2 }}
                    animate={{ scale: [1, 1.2, 1, 1.12, 1] }} transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.6, delay: 1.2 }}
                  >
                    <Heart size={16} color="#D62828" fill="#D62828" />
                  </motion.span>
                </span>
              </h2>
              <p style={{ color: '#6B7280', fontSize: 15.5, lineHeight: 1.8, marginTop: 16, maxWidth: 380 }}>
                From diagnostics to home care, our team is ready to assist. Call us or reach out on WhatsApp we're here 9 AM to 9 PM, all days.
              </p>
            </Reveal>

            <Reveal direction="right">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {[
                  { Icon: Phone, label: 'Call Now', detail: '+91 97898 90769', href: 'tel:+919789890769', color: '#005B66', bg: '#e8f4f5' },
                  { Icon: MessageCircle, label: 'WhatsApp Us', detail: 'Chat with our team', href: WA_URL, color: '#25D366', bg: '#e7f9ee', external: true },
                  { Icon: Mail, label: 'Send Enquiry', detail: 'reachgroups26@gmail.com', href: 'mailto:reachgroups26@gmail.com', color: '#D62828', bg: '#fdeaea' },
                ].map((c, i) => (
                  <motion.a
                    key={c.label}
                    href={c.href} target={c.external ? '_blank' : undefined} rel={c.external ? 'noopener noreferrer' : undefined}
                    initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                    whileHover={{ y: -4, boxShadow: '0 16px 34px rgba(0,20,22,0.14)' }}
                    className="sheen-panel"
                    style={{ display: 'flex', alignItems: 'center', gap: 14, borderRadius: 16, padding: '14px 20px', textDecoration: 'none' }}
                  >
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: c.bg, color: c.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <c.Icon size={20} />
                    </div>
                    <div>
                      <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: 15, color: '#1a1a1a' }}>{c.label}</div>
                      <div style={{ fontSize: 13, color: c.label === 'WhatsApp Us' ? '#25D366' : '#6B7280', fontWeight: c.label === 'WhatsApp Us' ? 600 : 400 }}>{c.detail}</div>
                    </div>
                  </motion.a>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
        <style>{`
          @media(max-width:900px){
            .cta-row{grid-template-columns:1fr!important; padding:0 20px!important; text-align:center;}
          }
        `}</style>
      </section>
    </>
  );
};

const ValueColumn: React.FC<{ item: typeof journey[number]; Icon: LucideIcon; index: number }> = ({ item, Icon, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
    style={{ textAlign: 'center', padding: '8px 6px' }}
  >
    <motion.div animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: index * 0.2 }} style={{ width: 46, height: 46, borderRadius: '50%', background: '#e8f4f5', color: '#005B66', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
      <Icon size={22} />
    </motion.div>
    <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: 14.5, color: '#1a1a1a', marginBottom: 4 }}>{item.title}</div>
    <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.5 }}>{item.sub}</div>
  </motion.div>
);

export default Home;